import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, getDoc, serverTimestamp
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

// ── Dark-teal theme ──────────────────────────────────────────────
const T = {
  bg:          "radial-gradient(ellipse at 15% 10%, #0a2a40 0%, #060d1f 55%, #030810 100%)",
  bgCard:      "rgba(10, 25, 50, 0.82)",
  teal:        "#00c9b1",
  tealBorder:  "rgba(0, 201, 177, 0.25)",
  tealGlow:    "rgba(0, 201, 177, 0.35)",
  text:        "#e0f7f4",
  textDim:     "rgba(160, 210, 220, 0.65)",
  red:         "linear-gradient(135deg, #ff4d4f, #ff6b6b)",
  green:       "linear-gradient(135deg, #00c9b1, #00e8cc)",
  inputBg:     "rgba(0, 201, 177, 0.07)",
  font:        "'Poppins', 'Segoe UI', sans-serif",
};

// ── Kannada dictionary ───────────────────────────────────────────
const tr = {
  en: {
    title:        "Vendor Dashboard",
    welcome:      "Welcome",
    logout:       "Logout",
    viewCard:     "View ID Card",
    profile:      "Your Profile",
    services:     "Your Services / Products",
    addNew:       "+ Add New",
    name:         "Name",
    desc:         "Description",
    price:        "Price (₹)",
    image:        "Image URL (optional)",
    save:         "Save",
    cancel:       "Cancel",
    edit:         "Edit",
    delete:       "Delete",
    deleteQ:      "Delete this service?",
    empty:        "No services yet. Add your first one!",
    category:     "Category",
    subcategory:  "Subcategory",
    subdistrict:  "Sub District",
    shop:         "Shop",
    phone:        "Phone",
  },
  kn: {
    title:        "ಮಾರಾಟಗಾರ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    welcome:      "ಸ್ವಾಗತ",
    logout:       "ಲಾಗ್ ಔಟ್",
    viewCard:     "ಐಡಿ ಕಾರ್ಡ್ ನೋಡಿ",
    profile:      "ನಿಮ್ಮ ಪ್ರೊಫೈಲ್",
    services:     "ನಿಮ್ಮ ಸೇವೆಗಳು / ಉತ್ಪನ್ನಗಳು",
    addNew:       "+ ಹೊಸದು ಸೇರಿಸಿ",
    name:         "ಹೆಸರು",
    desc:         "ವಿವರಣೆ",
    price:        "ಬೆಲೆ (₹)",
    image:        "ಚಿತ್ರ URL (ಐಚ್ಛಿಕ)",
    save:         "ಉಳಿಸಿ",
    cancel:       "ರದ್ದುಗೊಳಿಸಿ",
    edit:         "ಸಂಪಾದಿಸಿ",
    delete:       "ಅಳಿಸಿ",
    deleteQ:      "ಈ ಸೇವೆಯನ್ನು ಅಳಿಸಬೇಕೇ?",
    empty:        "ಇನ್ನೂ ಯಾವುದೇ ಸೇವೆಗಳಿಲ್ಲ. ನಿಮ್ಮ ಮೊದಲನೆಯದನ್ನು ಸೇರಿಸಿ!",
    category:     "ವರ್ಗ",
    subcategory:  "ಉಪ ವರ್ಗ",
    subdistrict:  "ಉಪ ಜಿಲ್ಲೆ",
    shop:         "ಅಂಗಡಿ",
    phone:        "ಫೋನ್",
  },
};

const districtKN = {
  Mandya: "ಮಂಡ್ಯ", Maddur: "ಮದ್ದೂರು", Malavalli: "ಮಳವಳ್ಳಿ",
  Srirangapatna: "ಶ್ರೀರಂಗಪಟ್ಟಣ", Nagamangala: "ನಾಗಮಂಗಲ",
  Krishnarajapete: "ಕೃಷ್ಣರಾಜಪೇಟೆ", Pandavapura: "ಪಾಂಡವಪುರ"
};

export default function VendorDashboard({ setUser, lang: langProp, setLang: setLangProp }) {
  const navigate = useNavigate();

  const [lang, setLang]               = useState(langProp || localStorage.getItem("lang") || "en");
  const [vendorData, setVendorData]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [services, setServices]       = useState([]);

  // Add/Edit form state
  const [editingId, setEditingId] = useState(null);   // null = add, else = edit that id
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ name: "", description: "", price: "", imageUrl: "" });

  const t = tr[lang];

  // ── Load vendor profile & access control ───────────────────────
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) { navigate("/", { replace: true }); return; }

      try {
        const snap = await getDoc(doc(db, "vendors", user.uid));
        if (!snap.exists()) { navigate("/", { replace: true }); return; }

        const data = snap.data();

        if (data.paymentStatus !== "submitted" && data.paymentStatus !== "approved") {
          navigate("/vendor-payment", { replace: true });
          return;
        }
        if (data.approvalStatus !== "approved") {
          navigate("/waiting-approval", { replace: true });
          return;
        }

        setVendorData({ ...data, uid: user.uid });
        setLoading(false);
      } catch (err) {
        console.error("Access check failed:", err);
        navigate("/", { replace: true });
      }
    };
    run();
  }, [navigate]);

  // ── Live list of this vendor's services ────────────────────────
  useEffect(() => {
    if (!vendorData?.uid) return;
    const q = query(collection(db, "services"), where("vendorId", "==", vendorData.uid));
    const unsub = onSnapshot(q, snap => {
      setServices(snap.docs.map(d => ({ ...d.data(), id: d.id })));
    }, err => console.error("services listen failed:", err));
    return () => unsub();
  }, [vendorData?.uid]);

  // ── Actions ────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm({ name: "", description: "", price: "", imageUrl: "" });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || "",
      description: s.description || "",
      price: s.price || "",
      imageUrl: s.imageUrl || "",
    });
    setShowForm(true);
  };

  const saveService = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert("Name is required"); return; }

    const payload = {
      vendorId:    vendorData.uid,
      vendorName:  vendorData.name || "",
      shopName:    vendorData.shopName || "",
      vendorPhone: vendorData.phone || "",
      category:    vendorData.category || "",
      subcategory: vendorData.subcategory || "",
      subdistrict: vendorData.subdistrict || "",
      name:        form.name.trim(),
      description: form.description.trim(),
      price:       form.price.trim(),
      imageUrl:    form.imageUrl.trim(),
      updatedAt:   serverTimestamp(),
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, "services", editingId), payload);
      } else {
        await addDoc(collection(db, "services"), { ...payload, createdAt: serverTimestamp() });
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: "", description: "", price: "", imageUrl: "" });
    } catch (err) {
      console.error("saveService failed:", err);
      alert("Save failed: " + (err?.code || "") + " " + (err?.message || err));
    }
  };

  const removeService = async (id) => {
    if (!window.confirm(t.deleteQ)) return;
    try {
      await deleteDoc(doc(db, "services", id));
    } catch (err) {
      console.error("deleteService failed:", err);
      alert("Delete failed: " + (err?.code || "") + " " + (err?.message || err));
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      if (setUser) setUser(null);
      navigate("/", { replace: true });
    } catch {
      alert("Logout failed");
    }
  };

  const toggleLang = () => {
    const next = lang === "en" ? "kn" : "en";
    setLang(next);
    localStorage.setItem("lang", next);
    if (setLangProp) setLangProp(next);
  };

  // ── UI ─────────────────────────────────────────────────────────
  if (loading || !vendorData) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh", color: T.text, fontFamily: T.font,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading…
      </div>
    );
  }

  const subdistrictDisplay = lang === "kn"
    ? (districtKN[vendorData.subdistrict] || vendorData.subdistrict)
    : vendorData.subdistrict;

  return (
    <div style={{ background: T.bg, minHeight: "100vh", color: T.text, fontFamily: T.font, padding: 16 }}>

      {/* HEADER */}
      <div style={{
        background: "linear-gradient(135deg, #0a2540 0%, #0d3060 100%)",
        border: `1px solid ${T.tealBorder}`, borderRadius: 18, padding: "14px 18px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: "0 6px 22px rgba(0,0,0,0.4)", marginBottom: 18,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>{t.title}</h2>
          <div style={{ color: T.textDim, fontSize: 13, marginTop: 2 }}>
            {t.welcome}, <span style={{ color: T.teal, fontWeight: 700 }}>{vendorData.name}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={toggleLang} style={btnGhost}>{lang === "en" ? "ಕನ್ನಡ" : "EN"}</button>
          <button onClick={() => navigate("/vendor-idcard")} style={btnGhost}>{t.viewCard}</button>
          <button onClick={handleLogout} style={btnDanger}>{t.logout}</button>
        </div>
      </div>

      {/* PROFILE CARD */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>{t.profile}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 10 }}>
          <Info label={t.shop}        value={vendorData.shopName} />
          <Info label={t.phone}       value={vendorData.phone} />
          <Info label={t.category}    value={(vendorData.category || "").replace(/_/g, " ")} />
          <Info label={t.subcategory} value={vendorData.subcategory} />
          <Info label={t.subdistrict} value={subdistrictDisplay} />
        </div>
      </div>

      {/* SERVICES */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={sectionTitle}>
            {t.services}
            <span style={countBadge}>{services.length}</span>
          </h3>
          <button onClick={openAdd} style={btnPrimary}>{t.addNew}</button>
        </div>

        {showForm && (
          <form onSubmit={saveService} style={formStyle}>
            <Field label={t.name}  value={form.name}        onChange={v => setForm({ ...form, name: v })} required />
            <Field label={t.desc}  value={form.description} onChange={v => setForm({ ...form, description: v })} multiline />
            <Field label={t.price} value={form.price}       onChange={v => setForm({ ...form, price: v })} />
            <Field label={t.image} value={form.imageUrl}    onChange={v => setForm({ ...form, imageUrl: v })} />
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button type="submit" style={{ ...btnPrimary, flex: 1 }}>{t.save}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} style={{ ...btnGhost, flex: 1 }}>
                {t.cancel}
              </button>
            </div>
          </form>
        )}

        <div style={{ marginTop: 14 }}>
          {services.length === 0 ? (
            <div style={{ color: T.textDim, padding: 24, textAlign: "center" }}>{t.empty}</div>
          ) : (
            services.map(s => (
              <div key={s.id} style={serviceCard}>
                {s.imageUrl && (
                  <img src={s.imageUrl} alt="" style={{
                    width: 64, height: 64, borderRadius: 10, objectFit: "cover",
                    border: `1px solid ${T.tealBorder}`, flexShrink: 0
                  }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: T.text }}>{s.name}</div>
                  {s.description && <div style={{ color: T.textDim, fontSize: 13, marginTop: 2 }}>{s.description}</div>}
                  {s.price && <div style={{ color: T.teal, fontSize: 13, marginTop: 2, fontWeight: 700 }}>₹{s.price}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => openEdit(s)} style={btnGhostSmall}>{t.edit}</button>
                  <button onClick={() => removeService(s.id)} style={btnDangerSmall}>{t.delete}</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ height: 30 }} />
    </div>
  );
}

// ── small components ───────────────────────────────────────────
function Info({ label, value }) {
  return (
    <div style={{
      background: "rgba(0, 201, 177, 0.06)",
      border: `1px solid ${T.tealBorder}`,
      borderRadius: 12, padding: "10px 12px",
    }}>
      <div style={{ color: T.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: T.text, fontWeight: 600, marginTop: 2, fontSize: 14 }}>{value || "—"}</div>
    </div>
  );
}

function Field({ label, value, onChange, required, multiline }) {
  const common = {
    width: "100%", padding: "10px 12px", borderRadius: 10,
    border: `1px solid ${T.tealBorder}`, background: T.inputBg,
    color: T.text, fontFamily: T.font, fontSize: 14, outline: "none",
    boxSizing: "border-box", marginTop: 4,
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ color: T.teal, fontWeight: 600, fontSize: 12 }}>{label}{required && " *"}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} style={common} />
      ) : (
        <input value={value} onChange={e => onChange(e.target.value)} style={common} required={required} />
      )}
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────
const cardStyle = {
  background: T.bgCard, border: `1px solid ${T.tealBorder}`,
  borderRadius: 18, padding: 16, boxShadow: "0 6px 22px rgba(0,0,0,0.35)",
  backdropFilter: "blur(10px)",
};
const sectionTitle = { margin: 0, fontSize: 16, color: T.text, display: "flex", alignItems: "center", gap: 8 };
const countBadge = {
  background: "rgba(0, 201, 177, 0.12)", color: T.teal, border: `1px solid ${T.tealBorder}`,
  padding: "1px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
};
const btnBase = {
  border: "none", padding: "9px 14px", borderRadius: 10, cursor: "pointer",
  fontWeight: 700, fontFamily: T.font, fontSize: 13,
};
const btnPrimary = { ...btnBase, background: T.green, color: "#060d1f", boxShadow: `0 4px 14px ${T.tealGlow}` };
const btnGhost   = { ...btnBase, background: "rgba(0,201,177,0.1)", color: T.teal, border: `1px solid ${T.tealBorder}` };
const btnDanger  = { ...btnBase, background: T.red, color: "#fff" };
const btnGhostSmall  = { ...btnGhost,  padding: "6px 10px", fontSize: 12 };
const btnDangerSmall = { ...btnDanger, padding: "6px 10px", fontSize: 12 };
const formStyle = {
  background: "rgba(0, 201, 177, 0.05)", border: `1px solid ${T.tealBorder}`,
  borderRadius: 14, padding: 14, marginTop: 12,
};
const serviceCard = {
  display: "flex", gap: 12, alignItems: "flex-start",
  background: "rgba(10, 30, 50, 0.6)", border: `1px solid ${T.tealBorder}`,
  borderRadius: 14, padding: 12, marginBottom: 10,
};